import { useEffect, useMemo, useState } from 'react';
import type { ApprovalFormField } from '../../types/approval';
import { selectCode, selectLabel } from '../../lib/approval-select';
import { previewBudget, type BudgetPreviewLineDto } from '../../api/budget';
import './BudgetHint.css';

/**
 * 기안 화면의 **예산 한 마디** (2026-09-04 · 예산 판 B4).
 *
 * ## ★★★ 왜 여기 있나
 *
 * 판 B3 까지는 **올린 뒤에야** 「예산에 못 붙었다」를 알았다. 그때는 이미 결재를 태운 뒤라
 * 고칠 수가 없다 — 회수하고 다시 쓰는 수밖에 없다. 물어볼 자리를 **앞으로** 옮긴다.
 *
 * ## ★★★ 막지 않는다 (결정 B5-a)
 *
 * *「그냥 올린다 — 초과를 표시만」*. 넘는다고 말할 뿐이고 올릴지는 사람이 정한다.
 * 그래서 이 조각은 **버튼을 잠그지 않고**, 붉은 오류로도 쓰지 않는다.
 *
 * ## ★★ 예산 «총액» 은 보여 주지 않는다
 *
 * 예산 화면은 회사 관리자 전용인데 기안은 누구나 한다 — 서버가 「넘는 만큼」만 주므로
 * 여기서도 그것만 그린다. 「300만원 중 20만원 남았다」가 아니라 **「20만원 넘습니다」**.
 *
 * ## ★ 조용히 접힌다
 *
 * 예산을 안 짠 회사 · 옛 서버(404) · 지출결의서가 아닌 양식 — 셋 다 **아무것도 안 그린다.**
 * 기안을 방해하지 않는 것이 이 조각의 첫째 규칙이다.
 */

/** 내역 표의 열 이름 — 서버 `expense-materialize.ts` 의 `ITEM_COLUMNS` 와 같다. */
const COL = { spentOn: '사용일', account: '계정과목', amount: '금액' } as const;

const won = (v: number) => `${v.toLocaleString()}원`;

/** 표 값에서 미리보기가 물을 줄들을 뽑는다. 하나도 못 뽑으면 빈 배열. */
export function linesFromForm(
  fields: readonly ApprovalFormField[],
  values: Record<string, unknown>,
): Array<{ seq: number; spentOn: string | null; accountCode: string | null; accountLabel: string | null; amount: number }> {
  const table = fields.find((f) => f.kind === 'table' && (f.columns ?? []).includes(COL.account));
  if (!table) return [];
  const cols = table.columns ?? [];
  const at = (name: string) => cols.indexOf(name);
  const iDate = at(COL.spentOn); const iAcc = at(COL.account); const iAmt = at(COL.amount);
  if (iAcc < 0 || iAmt < 0) return [];

  const rows = values[table.key];
  if (!Array.isArray(rows)) return [];

  const out: Array<{ seq: number; spentOn: string | null; accountCode: string | null; accountLabel: string | null; amount: number }> = [];
  let seq = 0;
  for (const r of rows as unknown[]) {
    if (!Array.isArray(r)) continue;
    const code = selectCode(r[iAcc]).trim();
    const amount = Number(String(r[iAmt] ?? '').replace(/,/g, ''));
    // ★ 아직 못 적은 줄은 묻지 않는다 — 「계정과목 없음」을 타이핑 중에 띄우면 시끄럽다.
    if (code === '' || !Number.isFinite(amount) || amount === 0) { seq += 1; continue; }
    out.push({
      seq,
      spentOn: iDate >= 0 ? (selectLabel(r[iDate]).trim() || null) : null,
      accountCode: code,
      accountLabel: selectLabel(r[iAcc]).trim() || null,
      amount,
    });
    seq += 1;
  }
  return out;
}

export function BudgetHint({ fields, values }: {
  fields: readonly ApprovalFormField[];
  values: Record<string, unknown>;
}) {
  const [result, setResult] = useState<BudgetPreviewLineDto[] | null>(null);

  const lines = useMemo(() => linesFromForm(fields, values), [fields, values]);
  const dept = useMemo(() => selectCode(values.dept).trim() || null, [values.dept]);
  /** 값이 실제로 바뀔 때만 다시 묻는다 — 타이핑마다 찌르지 않는다. */
  const key = useMemo(() => JSON.stringify([dept, lines]), [dept, lines]);

  useEffect(() => {
    if (lines.length === 0) { setResult(null); return; }
    let alive = true;
    // ★ 타이핑이 멎은 뒤에 묻는다 — 한 글자마다 서버를 찌르면 안 된다.
    const t = setTimeout(() => {
      previewBudget({ dept, lines })
        .then((r) => { if (alive) setResult(r); })
        // ★★ 옛 서버(404)·권한 어느 쪽이든 **조용히 접는다** — 기안을 방해하지 않는다.
        .catch(() => { if (alive) setResult(null); });
    }, 500);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!result || result.length === 0) return null;

  /** 넘는 것은 **예산 이름으로 묶는다** — 같은 예산의 여러 줄에 같은 값이 적혀 있다. */
  const overs = new Map<string, number>();
  for (const r of result) {
    if (r.over > 0 && r.budgetName) overs.set(r.budgetName, r.over);
  }
  const misses = result.filter((r) => r.miss).map((r) => `${r.seq + 1}번째 줄 — ${r.miss}`);
  const hits = result.filter((r) => r.budgetName && r.over === 0);

  if (overs.size === 0 && misses.length === 0 && hits.length === 0) return null;

  return (
    <div className="bhint" role="status">
      {overs.size > 0 && (
        <p className="bhint__over">
          {[...overs.entries()].map(([name, over]) => (
            <span key={name}>«{name}» 예산을 <b>{won(over)}</b> 넘습니다.</span>
          ))}
          {/* ★★ 막지 않는다 — 그 사실을 사람에게 분명히 말해 둔다. */}
          <small>올릴 수는 있습니다. 결재자가 이 사실을 함께 봅니다.</small>
        </p>
      )}

      {misses.length > 0 && (
        <ul className="bhint__miss">
          {misses.map((m) => <li key={m}>{m}</li>)}
        </ul>
      )}

      {overs.size === 0 && misses.length === 0 && hits.length > 0 && (
        <p className="bhint__ok">
          «{hits[0]!.budgetName}» 예산 안입니다.
        </p>
      )}
    </div>
  );
}
